/**
 * SSO: Callback de Google OAuth2
 *
 * Ruta: /auth/callback/google
 * Google redirige aquí con ?code=xxx después del consent screen.
 * Envía el code al backend para obtener JWT.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { useGoogleLogin, useGoogleOTPRedeem } from '@/modules/auth/hooks/useAuth'
import { rutaTrasLogin } from '@/modules/auth/rutaTrasLogin'
import type { LoginResponse } from '@/types'

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Acceso denegado. No se autorizó el inicio de sesión con Google.',
  account_inactive: 'Tu cuenta está desactivada. Contacta al administrador.',
  no_active_tenants: 'Tu cuenta no tiene acceso a ninguna empresa. Contacta al administrador.',
  invalid_tenant: 'Dominio de empresa inválido.',
  sso_not_configured: 'Google SSO no está configurado en la plataforma.',
  google_auth_failed: 'No se pudo autenticar con Google. Intenta de nuevo.',
  invalid_token: 'El token de Google es inválido.',
  no_email: 'No se pudo obtener el email de la cuenta de Google.',
}

export function GoogleCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutate: googleLogin } = useGoogleLogin()
  const { mutate: redeemOTP } = useGoogleOTPRedeem()
  const [error, setError] = useState<string | null>(null)
  const [processed, setProcessed] = useState(false)

  function handleLoginSuccess(response: LoginResponse) {
    navigate(rutaTrasLogin(response), { replace: true })
  }

  function handleLoginError(err: unknown) {
    const apiError = err as { response?: { data?: { error?: { message?: string } } } }
    setError(
      apiError?.response?.data?.error?.message
        || 'Error al iniciar sesión con Google. Intenta de nuevo.'
    )
  }

  useEffect(() => {
    if (processed) return
    setProcessed(true)

    const token = searchParams.get('token')
    const code = searchParams.get('code')
    const errorParam = searchParams.get('error')

    if (errorParam) {
      setError(ERROR_MESSAGES[errorParam] ?? `Error de autenticación: ${errorParam}`)
      return
    }

    // Modo OTP: el backend ya hizo el intercambio con Google (staging/prod)
    if (token) {
      redeemOTP({ token }, { onSuccess: handleLoginSuccess, onError: handleLoginError })
      return
    }

    // Modo código directo: solo en dev (window.location.origin === redirect_uri)
    if (!code) {
      setError('No se recibió código de autorización de Google.')
      return
    }

    // El backend exige el `state` que dejó en la cookie al iniciar el login: así
    // un `code` que llegó en una URL ajena no deja a nadie en la cuenta de otro.
    googleLogin(
      { code, state: searchParams.get('state') ?? '' },
      { onSuccess: handleLoginSuccess, onError: handleLoginError },
    )
  }, [searchParams, googleLogin, redeemOTP, navigate, processed])

  return (
    <div className="min-h-screen flex items-center justify-center bg-auth px-4">
      <div className="w-full max-w-sm">
        <div className="bg-surface rounded-2xl shadow-2xl p-8 text-center">
          {error ? (
            <>
              <div className="w-12 h-12 rounded-full bg-danger-surface-strong flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={24} className="text-danger-solid" />
              </div>
              <h2 className="text-lg font-semibold text-content mb-2">
                Error de autenticación
              </h2>
              <p className="text-sm text-content-muted mb-6">{error}</p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center px-6 py-2.5 bg-primary hover:bg-primary-hover
                  text-on-primary text-sm font-medium rounded-lg transition-colors"
              >
                Volver al login
              </Link>
            </>
          ) : (
            <>
              <Loader2 size={32} className="animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-content mb-1">
                Autenticando con Google
              </h2>
              <p className="text-sm text-content-subtle">
                Un momento...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
